/**
 * What identifies a branch — one definition, for every validator.
 *
 * A `parallel_fork` or `switch_case` declares its branches in config, and the
 * edges leaving it carry a port naming which branch they belong to. Deciding
 * whether a declared branch is actually wired means answering one question:
 * does any outgoing edge's port name this branch?
 *
 * Three validators answered it three different ways, and two of them were
 * wrong:
 *
 *   utils/v2/v2Lint.js      b.name || b.id || b.label || b.value    correct
 *   pages/v3/v3Validate.js  label / value / name                    no id
 *   pages/v4/v4Validate.js  label || value || name, exact match only no id,
 *                                                                   case-sensitive
 *
 * The platform generates forks whose branches carry BOTH an id and a human
 * label:
 *
 *   config.branches: [{ id: 'branch_1', label: 'ORG · block api keys in input' }]
 *   edges:            fromPort: 'branch_1'
 *
 * So v3Validate looked for an edge on port "ORG · block api keys in input",
 * found none, and reported a fully-wired branch as dangling. On a governance
 * agent with seven policy gates that is seven false warnings, each offered to
 * the operator with a one-click "Ask AI to fix these" whose own suggestion is
 * to drop a node into the branch or delete it from config — either of which
 * would damage a correct graph.
 *
 * This is the third instance of one pattern. v3Validate's own comments record
 * the first two: reading `cfg.legs` when the schema says `cfg.branches`
 * (RC-112d), and matching `label` when the edge carries `value`. Each was
 * fixed by adding the missing alias to a hardcoded list, in one file. This
 * module exists so the list stops being hardcoded in more than one place.
 */

/**
 * Every port name that legitimately identifies this branch, lowercased
 * alongside its original spelling.
 *
 * Case-insensitivity matters because the AI Workflow Builder and hand-authored
 * canvases disagree about capitalisation — `case: {label:'Critical',
 * value:'critical'}` against `edge: {fromPort:'critical'}` is the documented
 * shape that first broke this rule.
 *
 * @param {string|object} branch a config.branches / config.cases entry
 * @returns {Set<string>} acceptable port identifiers; empty when unidentifiable
 */
export function branchIdentifiers(branch) {
  const accepted = new Set();
  if (typeof branch === 'string') {
    if (branch) { accepted.add(branch); accepted.add(branch.toLowerCase()); }
    return accepted;
  }
  if (!branch || typeof branch !== 'object') return accepted;

  // `id` first because it is what the platform's own fork generator emits as
  // the edge port; the rest are authoring aliases.
  for (const key of ['id', 'name', 'label', 'value']) {
    const v = branch[key];
    if (typeof v === 'string' && v) {
      accepted.add(v);
      accepted.add(v.toLowerCase());
    }
  }
  return accepted;
}

/**
 * The name to SHOW an operator for this branch.
 *
 * Label first — deliberately the opposite preference to matching. A warning
 * that says `branch_3` tells the operator nothing; one that says
 * "ORG · block ssn in user input" points at the thing on their screen.
 */
export function branchDisplayName(branch) {
  if (typeof branch === 'string') return branch;
  if (!branch || typeof branch !== 'object') return '';
  for (const key of ['label', 'value', 'name', 'id']) {
    const v = branch[key];
    if (typeof v === 'string' && v) return v;
  }
  return '';
}

/**
 * Does this connection leave the fork on the port belonging to this branch?
 *
 * Checks every field a port has been spelled in across the codebase's history.
 * `label` is included because some importers put the branch name there rather
 * than on a port field.
 */
export function edgeMatchesBranch(connection, branch) {
  const accepted = branchIdentifiers(branch);
  if (accepted.size === 0) return false;
  const candidates = [
    connection && connection.fromPort,
    connection && connection.sourcePort,
    connection && connection.sourceHandle,
    connection && connection.label,
  ].filter((p) => typeof p === 'string' && p);
  return candidates.some((p) => accepted.has(p) || accepted.has(p.toLowerCase()));
}

/** True when at least one of `connections` wires this branch. */
export function branchIsWired(connections, branch) {
  return (connections || []).some((c) => edgeMatchesBranch(c, branch));
}
